import React, { useState, useMemo } from 'react'
import './twoMinuteAnalysis.css'

/**
 * The 2-Minute Analysis
 * A quick back-of-the-envelope valuation tool for a deal.
 *
 * Formulas (mirrors the AbbeySquare deal-analysis spreadsheet):
 *   Net Annual Income   = Gross Potential Annual Income × (1 − Vacancy %)
 *   Net Operating Income = Net Annual Income × (1 − Expense Rate %)
 *   Valuation           = Net Operating Income ÷ Market Cap Rate
 *
 * Self-contained: holds its own state and styles, touches no other module files.
 */

const DEFAULTS = {
  grossPotentialIncome: '',
  vacancyRate: '',
  expenseRate: '',
  capRate: '',
}

const currency = (n) =>
  Number.isFinite(n)
    ? n.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      })
    : '—'

export function TwoMinuteAnalysis({ initialValues = {}, onChange }) {
  const [values, setValues] = useState({ ...DEFAULTS, ...initialValues })

  const handleChange = (key) => (e) => {
    const raw = e.target.value
    const next = { ...values, [key]: raw === '' ? '' : Number(raw) }
    setValues(next)
    if (onChange) onChange(next)
  }

  const { netAnnualIncome, netOperatingIncome, valuation } = useMemo(() => {
    const hasGpi = values.grossPotentialIncome !== ''
    const hasVac = values.vacancyRate !== ''
    const hasExp = values.expenseRate !== ''
    const hasCap = values.capRate !== ''

    const gpi = Number(values.grossPotentialIncome)
    const vac = Number(values.vacancyRate) / 100
    const exp = Number(values.expenseRate) / 100
    const cap = Number(values.capRate) / 100

    if (!hasGpi || !hasVac) {
      return { netAnnualIncome: NaN, netOperatingIncome: NaN, valuation: NaN }
    }

    const nai = gpi * (1 - vac)
    const noi = hasExp ? nai * (1 - exp) : NaN
    const val = hasExp && hasCap && cap > 0 ? noi / cap : NaN
    return { netAnnualIncome: nai, netOperatingIncome: noi, valuation: val }
  }, [values])

  return (
    <div className="tma form-section">
      <header className="tma-header">
        <h2>The 2-Minute Analysis</h2>
        <p className="section-description">
          A quick valuation pass. Adjust the inputs below — totals recalculate instantly.
        </p>
      </header>

      <div className="tma-card">
        <div className="tma-row">
          <label htmlFor="tma-gpi">Gross Potential Annual Income</label>
          <div className="tma-input input-prefix">
            <span>$</span>
            <input
              id="tma-gpi"
              type="number"
              min="0"
              value={values.grossPotentialIncome}
              onChange={handleChange('grossPotentialIncome')}
              className="form-input"
            />
          </div>
          <span className="tma-hint">Use info from the marketing package.</span>
        </div>

        <div className="tma-row">
          <label htmlFor="tma-vac">− Economic &amp; Physical Vacancy</label>
          <div className="tma-input input-suffix">
            <input
              id="tma-vac"
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={values.vacancyRate}
              onChange={handleChange('vacancyRate')}
              className="form-input"
            />
            <span>%</span>
          </div>
          <span className="tma-hint">Rule of thumb — consider carefully before changing.</span>
        </div>

        <div className="tma-row tma-result">
          <label>= Net Annual Income</label>
          <div className="tma-value">{currency(netAnnualIncome)}</div>
          <span className="tma-hint" />
        </div>

        <div className="tma-row">
          <label htmlFor="tma-exp">− Annual Expense Rate</label>
          <div className="tma-input input-suffix">
            <input
              id="tma-exp"
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={values.expenseRate}
              onChange={handleChange('expenseRate')}
              className="form-input"
            />
            <span>%</span>
          </div>
          <span className="tma-hint">Rule of thumb — carefully consider before changing.</span>
        </div>

        <div className="tma-row tma-result">
          <label>= Net Operating Income</label>
          <div className="tma-value">{currency(netOperatingIncome)}</div>
          <span className="tma-hint" />
        </div>

        <div className="tma-row">
          <label htmlFor="tma-cap">Market Cap Rate</label>
          <div className="tma-input input-suffix tma-input-highlight">
            <input
              id="tma-cap"
              type="number"
              min="0"
              step="0.1"
              value={values.capRate}
              onChange={handleChange('capRate')}
              className="form-input"
            />
            <span>%</span>
          </div>
          <span className="tma-hint">Use cap rate from the marketing package or ask the broker.</span>
        </div>

        <div className="tma-row tma-valuation">
          <label>Valuation</label>
          <div className="tma-value">{currency(valuation)}</div>
          <span className="tma-hint" />
        </div>
      </div>

      <p className="tma-disclaimer">
        The information herein is not guaranteed but believed to be accurate.
      </p>
    </div>
  )
}

export default TwoMinuteAnalysis
